<template>
  <div>
    <div
      v-if="!showDetail"
      class="bg-white mb-[24px]"
    >
      <div class="flex items-center justify-between gap-[8px] mb-[24px] px-[20px] py-[16px] border-b-[1px] border-b-[#E5E5E5]">
        <div class="flex items-center gap-[8px] shrink-1">
          <Button
            v-if="userData.is_komship === 1 && userData.is_kompack === 1"
            outline
            type="icon"
            @click="$emit('back')"
          >
            <span class="k-arrow-left-2 text-[#0A0A0A]" />
          </Button>
          <span class="text-[#0A0A0A] font-semibold">
            Riwayat Pengajuan Packing
          </span>
        </div>
        <DateRangeFilter
          v-model="dateRange"
          :max-date="today"
          class="max-w-[300px] mt-[5px]"
        />
      </div>
      <div class="flex flex-col sm:flex-row items-center justify-between gap-[16px] mb-[16px] px-[20px]">
        <div class="w-full sm:max-w-[250px]">
          <UIInput
            v-model="searchAwb"
            placeholder="Cari Outbond ID"
          >
            <template #suffix>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#737373"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                />
                <line
                  x1="21"
                  y1="21"
                  x2="16.65"
                  y2="16.65"
                />
              </svg>
            </template>
          </UIInput>
        </div>
        <div class="w-full sm:max-w-[300px]">
          <Select
            v-model="warehouse"
            :options="warehouseOptions"
            :reduce="option => option"
            :clearable="false"
            option-label="name"
            placeholder="Semua Gudang"
            :has-next-page="fetcherListWarehouses.hasMore"
            :loading="fetcherListWarehouses.isLoading && fetcherListWarehouses.offset === 0"
            :debounce-timeout="500"
            @on-search="onSearchWarehouse"
            @on-intersect="fetcherListWarehouses.fetchData()"
          />
        </div>
      </div>
      <div class="px-[20px] pb-[20px]">
        <Table
          :items="fetcherPackingHistory.data"
          :loading="fetcherPackingHistory.isLoading"
          :fetcher="fetcherPackingHistory"
          :empty-text="emptyText"
        />
      </div>
    </div>
    <div v-if="showDetail">
      <DetailPacking
        :detail-id="detailId"
        @back="showDetail = false"
      />
    </div>
  </div>
</template>

<script>
import {
  YEAR_MONTH_DAY, DAY_WITH_FULL_DATE, DAY_MONTH_YEAR, TIME,
} from '@/libs/formatDate'
import { today, kompackDate } from '@/store/helpers'
import { Button, Input as UIInput, Select } from '@/@core/components/ui'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import DetailPacking from './history/detail/DetailPacking.vue'
import DateRangeFilter from './history/components/DateRangeFilter.vue'
import Table from './history/components/Table.vue'

export default {
  components: {
    DateRangeFilter,
    DetailPacking,
    Table,
    Button,
    UIInput,
    Select,
  },
  props: {
    userData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showDetail: false,
      detailId: '',
      searchAwb: '',
      debounceTimer: null,

      warehouse: {
        name: 'Semua Gudang',
        id: '',
        mitra_id: '',
      },

      dateRange: {
        startDate: kompackDate,
        endDate: today,
      },
      DAY_WITH_FULL_DATE,
      YEAR_MONTH_DAY,
      TIME,
      kompackDate,
      today,
    }
  },
  computed: {
    warehouseOptions() {
      const defaultOption = { name: 'Semua Gudang', id: '', mitra_id: '' }
      const keyword = (this.fetcherListWarehouses?.options?.keyword || '').trim().toLowerCase()
      const shouldIncludeDefault = !keyword || 'semua gudang'.includes(keyword)

      const list = this.fetcherListWarehouses?.data || []
      const seenIds = new Set()
      const uniqueData = list.filter(item => {
        if (!item || item.id === undefined || item.id === null) return false
        if (seenIds.has(item.id)) return false
        seenIds.add(item.id)
        return true
      })

      return shouldIncludeDefault ? [defaultOption, ...uniqueData] : uniqueData
    },
    emptyText() {
      const hasSearch = Boolean(this.searchAwb && this.searchAwb.trim())
      const hasWarehouse = Boolean(this.warehouse && (this.warehouse.id || this.warehouse.mitra_id))
      const hasDate = Boolean(this.dateRange && (
        YEAR_MONTH_DAY(this.dateRange.startDate) !== YEAR_MONTH_DAY(kompackDate)
        || YEAR_MONTH_DAY(this.dateRange.endDate) !== YEAR_MONTH_DAY(today)
      ))

      if (!hasSearch && !hasWarehouse && !hasDate) {
        return 'Tidak ada data yang dapat ditampilkan.'
      }

      const searchVal = this.searchAwb ? this.searchAwb.trim() : ''
      const warehouseName = this.warehouse?.name || 'Semua Gudang'

      let dateStr = ''
      if (hasDate) {
        const startStr = DAY_MONTH_YEAR(this.dateRange.startDate)
        const endStr = DAY_MONTH_YEAR(this.dateRange.endDate)
        dateStr = startStr === endStr ? startStr : `${startStr} - ${endStr}`
      }

      const subject = hasSearch ? `Data dengan Outbound ID "${searchVal}" tidak ditemukan` : 'Data tidak ditemukan'
      const location = hasSearch || hasWarehouse ? ` di ${warehouseName}` : ''
      const date = hasDate ? ` pada tanggal ${dateStr}` : ''

      return `${subject}${location}${date}`
    },
  },
  watch: {
    searchAwb(newVal) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.fetcherPackingHistory.fetchData({
          reset: true,
          options: {
            warehouse_id: this.warehouse ? this.warehouse.id : '',
            start_date: YEAR_MONTH_DAY(this.dateRange.startDate),
            end_date: YEAR_MONTH_DAY(this.dateRange.endDate),
            search: newVal,
          },
        })
      }, 500)
    },
    warehouse: {
      handler(newVal) {
        this.fetcherPackingHistory.fetchData({
          reset: true,
          options: {
            warehouse_id: newVal ? newVal.id : '',
            start_date: YEAR_MONTH_DAY(this.dateRange.startDate),
            end_date: YEAR_MONTH_DAY(this.dateRange.endDate),
            search: this.searchAwb,
          },
        })
      },
      deep: true,
    },
    dateRange: {
      handler(newVal) {
        this.fetcherPackingHistory.fetchData({
          reset: true,
          options: {
            warehouse_id: this.warehouse ? this.warehouse.id : '',
            start_date: YEAR_MONTH_DAY(newVal.startDate),
            end_date: YEAR_MONTH_DAY(newVal.endDate),
            search: this.searchAwb,
          },
        })
      },
      deep: true,
    },
  },
  created() {
    this.initializeFetcherPackingHistory()
    this.initializeWarehouseList()
  },
  beforeMount() {
    this.fetcherListWarehouses.fetchData({ reset: true })
  },
  mounted() {
    this.fetcherPackingHistory.fetchData({
      reset: true,
      options: {
        warehouse_id: this.warehouse ? this.warehouse.id : '',
        start_date: YEAR_MONTH_DAY(this.dateRange.startDate),
        end_date: YEAR_MONTH_DAY(this.dateRange.endDate),
        search: this.searchAwb,
      },
    })
  },
  methods: {
    initializeFetcherPackingHistory() {
      const fnFetch = ({
        offset, limit, warehouse_id, start_date, end_date, search,
      }) => this.$http_new.get('/kompack/api/v1/outbounds', {
        params: {
          warehouse_id,
          start_date,
          end_date,
          outbound_id: search,
          offset,
          limit,
        },
      }).then(res => {
        const { data } = res.data
        if (!data) {
          return {
            data: [],
          }
        }

        return {
          data,
        }
      }).catch(() => {
        this.$toast_error({
          message: 'Gagal mengambil data produk, silahkan coba lagi',
        })
        return {
          data: [],
        }
      })

      this.fetcherPackingHistory = createPaginatedFetcher(fnFetch, 0, 50)
    },
    initializeWarehouseList() {
      const fnFetch = ({
        offset, limit, keyword,
      }) => this.$http_new.get(`/komship/api/v1/warehouses/partners/${this.userData.partner_detail.id}`, {
        params: {
          page: offset,
          limit,
          keyword,
        },
      }).then(res => {
        const { data } = res.data
        return {
          data: data || [],
        }
      }).catch(() => {
        this.$toast_error({
          message: 'Gagal mengambil data warehouse, silahkan coba lagi',
        })
        return {
          data: [],
        }
      })

      this.fetcherListWarehouses = createPaginatedFetcher(fnFetch, 0, 20)
    },
    onSearchWarehouse(query) {
      this.fetcherListWarehouses.fetchData({
        reset: true,
        options: {
          keyword: query,
        },
      })
    },
    setStatusBadgeVariant(status) {
      if (status === 'submitted') {
        return {
          value: 'Diajukan',
          variant: 'light-info',
        }
      }
      if (status === 'canceled') {
        return {
          value: 'Dibatalkan',
          variant: 'light-secondary',
        }
      }
      return {
        value: 'Dipacking',
        variant: 'light-warning',
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
